import {isNil, isNotEmpty, prop, trim, uniq, uniqBy} from 'ramda';

import type {SellerResponseBody} from '@dms/api/core';
import {parseSellerPhoneNumber} from '@dms/shared';

import type {SellerContact} from '../../../types/SellerContact';

/**
 * Formats the seller phone numbers for display and builds a `tel:` link for each of them.
 *
 * Alpha sends all numbers of a seller in a single comma separated string and in mixed shapes -
 * `+49 371 52232685`, `004937152232685`, a bare `4937152232685` or a national `0371 5223268`.
 * Every number is formatted only when it can be read as a valid phone number; anything else is
 * kept exactly as it came and is not linked, because a guessed dial code is worse than an
 * unformatted number - and dialing it would call a stranger.
 */
export function getSellerPhoneNumbers(seller: SellerResponseBody): SellerContact[] {
  const rawNumbers = uniq(seller.phone?.split(',').map(trim).filter(isNotEmpty) ?? []);
  const countryAlpha3 = seller.country?.key;

  return uniqBy(
    prop('label'),
    rawNumbers.map((rawNumber) => toPhoneContact(rawNumber, countryAlpha3))
  );
}

function toPhoneContact(rawNumber: string, countryAlpha3: string | undefined): SellerContact {
  const phoneNumber = parseSellerPhoneNumber(rawNumber, countryAlpha3);

  if (isNil(phoneNumber)) {
    return {label: rawNumber};
  }

  return {label: phoneNumber.formatInternational(), href: phoneNumber.getURI()};
}
